import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  RECORD_KINDS,
  groupRecords,
  recordDue,
  recordDueState,
  recordFields,
  recordPlanCurrent,
  recordTitle,
  telHref,
} from '@kraftwerk/shared';
import type { RecordField, RecordKind, RecordVisibility } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import type { LocalRecordEntry } from '../../db/local.js';
import {
  deleteRecordEntry,
  heldRecordCount,
  InvalidRecord,
  planRecordDeadline,
  planStillThere,
  recordsOf,
  saveRecordEntry,
} from '../../db/records.js';
import { useToday } from '../../hooks/useToday.js';
import { formatDay } from '../../format.js';

/**
 * Akte einer Person (27.10.2026): schmale Liste nach Art, Zeile tippen klappt
 * auf, Bearbeiten und Löschen stehen erst dort. Fehlt eine Angabe, steht
 * „Keine Angabe“ da. Fristen lassen sich als Aufgabe oder Termin in die
 * Planung legen.
 *
 * - `own`: die eigene Akte (privat, ohne Haushalt).
 * - `child`: die Akte eines Kindes, von Erwachsenen geführt.
 * - `readOnly`: das Kind sieht, was die Eltern freigegeben haben.
 */
export function RecordPanel({
  subjectId,
  householdId,
  mode,
  name,
  onSetupEncryption,
}: {
  subjectId: string;
  householdId: string | null;
  mode: 'own' | 'child' | 'readOnly';
  /** Name des Kindes — für Sichtbarkeit und Vorschläge. */
  name?: string;
  /** Eigene Akte ohne Kontoschlüssel: dorthin, wo man ihn einrichtet. */
  onSetupEncryption?: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const entries = useLiveQuery(
    () => recordsOf(subjectId, mode === 'readOnly' ? undefined : householdId, db),
    [subjectId, householdId, mode],
    [] as LocalRecordEntry[],
  );
  const held = useLiveQuery(
    () => (mode === 'readOnly' ? 0 : heldRecordCount(householdId, db)),
    [householdId, mode],
    0,
  );
  const [open, setOpen] = useState<string | null>(null);
  const [editing, setEditing] = useState<LocalRecordEntry | null>(null);
  const [adding, setAdding] = useState<RecordKind | 'pick' | null>(null);
  const groups = groupRecords(entries);
  const mayWrite = mode !== 'readOnly';

  return (
    <div className="flex flex-col gap-3">
      {held > 0 ? (
        <div role="status" className="flex flex-col gap-2 rounded-lg border border-warn p-2">
          <p className="text-xs text-warn">
            {householdId
              ? t('record.heldHousehold', { count: held })
              : t('record.heldOwn', { count: held })}
          </p>
          {!householdId && onSetupEncryption ? (
            <button
              type="button"
              onClick={onSetupEncryption}
              className="kw-btn kw-btn-quiet kw-btn-small self-start"
            >
              {t('record.toEncryption')}
            </button>
          ) : null}
        </div>
      ) : null}

      {groups.length === 0 ? (
        <p className="text-sm text-ink-muted">
          {mode === 'readOnly' ? t('record.emptyShared') : t('record.empty')}
        </p>
      ) : (
        groups.map((group) => (
          <section key={group.kind} aria-label={t(`record.kinds.${group.kind}`)}>
            <h4 className="kw-label">{t(`record.kinds.${group.kind}`)}</h4>
            <ul className="mt-1 flex flex-col gap-1">
              {group.entries.map((entry) => (
                <li key={entry.id}>
                  {editing?.id === entry.id ? (
                    <EntryForm
                      kind={entry.kind}
                      existing={entry}
                      subjectId={subjectId}
                      householdId={householdId}
                      mode={mode}
                      name={name}
                      onDone={() => setEditing(null)}
                    />
                  ) : (
                    <EntryRow
                      entry={entry}
                      open={open === entry.id}
                      onToggle={() => setOpen(open === entry.id ? null : entry.id)}
                      mayWrite={mayWrite}
                      name={name}
                      onEdit={() => setEditing(entry)}
                    />
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))
      )}

      {mayWrite ? (
        adding === null ? (
          <button
            type="button"
            onClick={() => setAdding('pick')}
            className="kw-btn kw-btn-quiet kw-btn-small"
          >
            {t('record.add')}
          </button>
        ) : adding === 'pick' ? (
          <fieldset className="flex flex-col gap-2 rounded-lg border border-line p-2">
            <legend className="kw-label px-1">{t('record.pickKind')}</legend>
            <div className="flex flex-wrap gap-2">
              {RECORD_KINDS.map((kind) => (
                <button
                  key={kind}
                  type="button"
                  onClick={() => setAdding(kind)}
                  className="kw-toggle"
                >
                  {t(`record.kinds.${kind}`)}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setAdding(null)}
              className="self-start py-1 text-xs text-ink-muted underline underline-offset-2"
            >
              {t('record.cancel')}
            </button>
          </fieldset>
        ) : (
          <EntryForm
            kind={adding}
            existing={null}
            subjectId={subjectId}
            householdId={householdId}
            mode={mode}
            name={name}
            onDone={() => setAdding(null)}
          />
        )
      ) : null}
    </div>
  );
}

function EntryRow({
  entry,
  open,
  onToggle,
  mayWrite,
  name,
  onEdit,
}: {
  entry: LocalRecordEntry;
  open: boolean;
  onToggle: () => void;
  mayWrite: boolean;
  name: string | undefined;
  onEdit: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const today = useToday();
  const [confirming, setConfirming] = useState(false);
  const due = recordDue(entry.kind, entry.fields);
  const state = due && today ? recordDueState(due, today) : null;
  const panelId = useId();

  return (
    <div className="rounded-lg border border-line">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
        className="flex min-h-11 w-full items-center justify-between gap-3 px-3 py-2 text-left"
      >
        <span className="min-w-0 truncate text-sm font-medium">
          {recordTitle(entry.kind, entry.fields)}
        </span>
        {due ? (
          <span
            className={`shrink-0 text-xs ${
              state === 'overdue'
                ? 'text-danger'
                : state === 'soon'
                  ? 'text-warn'
                  : 'text-ink-muted'
            }`}
          >
            {t(`record.due.${state ?? 'later'}`, { date: formatDay(due, 'short', i18n.language) })}
          </span>
        ) : null}
      </button>
      {open ? (
        <div id={panelId} className="flex flex-col gap-2 border-t border-line px-3 py-2">
          <dl className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-x-3 gap-y-1 text-sm">
            {recordFields(entry.kind)
              .filter((field) => !('primary' in field))
              .map((field) => (
                <FieldValue key={field.key} entry={entry} field={field} />
              ))}
          </dl>
          {entry.householdId && mayWrite ? (
            <p className="text-xs text-ink-muted">
              {t(`record.visibility.${entry.visibility === 'child' ? 'child' : 'adults'}`, {
                name: name ?? '',
              })}
            </p>
          ) : null}
          {due && mayWrite ? <DeadlinePlan entry={entry} due={due} name={name} /> : null}
          {mayWrite ? (
            confirming ? (
              <div className="flex flex-col gap-2 rounded-lg border border-danger p-2">
                <p className="text-sm">{t('record.deleteWarning')}</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => void deleteRecordEntry(entry, db)}
                    className="kw-btn kw-btn-small flex-1 bg-danger"
                  >
                    {t('record.deleteConfirm')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirming(false)}
                    className="kw-btn kw-btn-quiet kw-btn-small flex-1"
                  >
                    {t('record.cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={onEdit} className="kw-btn kw-btn-quiet kw-btn-small">
                  {t('record.edit')}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirming(true)}
                  className="py-2 text-xs text-danger underline underline-offset-2"
                >
                  {t('record.delete')}
                </button>
              </div>
            )
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function FieldValue({
  entry,
  field,
}: {
  entry: LocalRecordEntry;
  field: RecordField;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const value = entry.fields[field.key];
  const href = value && field.type === 'phone' ? telHref(value) : null;
  return (
    <>
      <dt className="text-ink-muted">{t(`record.fields.${entry.kind}.${field.key}`)}</dt>
      <dd className="min-w-0 break-words whitespace-pre-wrap">
        {!value ? (
          <span className="text-ink-muted">{t('record.noValue')}</span>
        ) : href ? (
          <a href={href} className="underline underline-offset-2">
            {value}
          </a>
        ) : field.type === 'date' ? (
          // Mit Jahr: „Geimpft am Mi., 10.11.“ las sich wie nächste Woche,
          // war aber 2021 (Geräteabnahme 28.10.2026).
          formatDay(value, 'shortYear', i18n.language)
        ) : (
          value
        )}
      </dd>
    </>
  );
}

/**
 * Frist in die Planung: als Aufgabe (mit Uhrzeit kommt eine Mitteilung) oder
 * als Termin. Der Titel geht lesbar zum Server — der Hinweis sagt das, der
 * Vorschlag nennt deshalb nur die Art, nicht den Inhalt.
 */
function DeadlinePlan({
  entry,
  due,
  name,
}: {
  entry: LocalRecordEntry;
  due: string;
  name: string | undefined;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const dateId = useId();
  const timeId = useId();
  const there = useLiveQuery(() => planStillThere(entry.plan, db), [entry.plan], false);
  const [form, setForm] = useState(false);
  const [as, setAs] = useState<'task' | 'event'>('task');
  const suggestion = name
    ? t('record.planTitleFor', { kind: t(`record.kinds.${entry.kind}`), name })
    : t('record.planTitle', { kind: t(`record.kinds.${entry.kind}`) });
  const [title, setTitle] = useState(suggestion);
  const [date, setDate] = useState(due);
  const [time, setTime] = useState('09:00');

  if (there && entry.plan && recordPlanCurrent(due, entry.plan)) {
    return (
      <p className="text-xs text-good">
        {t(`record.planned.${entry.plan.kind}`, {
          date: formatDay(due, 'short', i18n.language),
        })}
      </p>
    );
  }
  if (!form) {
    return (
      <div className="flex flex-col gap-1">
        {there && entry.plan ? (
          <p className="text-xs text-warn">{t('record.planOutdated')}</p>
        ) : null}
        <button
          type="button"
          onClick={() => setForm(true)}
          className="kw-btn kw-btn-small self-start"
        >
          {t('record.plan')}
        </button>
      </div>
    );
  }
  return (
    <form
      className="flex flex-col gap-2 rounded-lg bg-surface-raised p-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (!title.trim() || !date) return;
        void planRecordDeadline(
          entry,
          {
            as,
            title,
            date,
            time: as === 'task' && time ? time : null,
            newListName: t('record.taskList'),
          },
          db,
        ).then(() => setForm(false));
      }}
    >
      <fieldset className="flex flex-col gap-1">
        <legend className="text-xs text-ink-muted">{t('record.planAs')}</legend>
        <div className="flex flex-wrap gap-2">
          {(['task', 'event'] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={as === option}
              onClick={() => setAs(option)}
              className="kw-toggle"
            >
              {t(`record.planKinds.${option}`)}
            </button>
          ))}
        </div>
      </fieldset>
      <label htmlFor={titleId} className="flex flex-col gap-1">
        <span className="text-xs text-ink-muted">{t('record.planTitleLabel')}</span>
        <input
          id={titleId}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          maxLength={300}
          required
          className="kw-field"
        />
      </label>
      <p className="text-xs text-ink-muted">{t('record.planServerHint')}</p>
      <div className="flex flex-wrap gap-2">
        <label htmlFor={dateId} className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-xs text-ink-muted">{t('record.planDate')}</span>
          <input
            id={dateId}
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            required
            className="kw-field"
          />
        </label>
        {as === 'task' ? (
          <label htmlFor={timeId} className="flex w-32 flex-col gap-1">
            <span className="text-xs text-ink-muted">{t('record.planTime')}</span>
            <input
              id={timeId}
              type="time"
              value={time}
              onChange={(event) => setTime(event.target.value)}
              className="kw-field"
            />
          </label>
        ) : null}
      </div>
      <p className="text-xs text-ink-muted">
        {as === 'task'
          ? time
            ? t('record.planTaskHint')
            : t('record.planTaskNoTime')
          : t('record.planEventHint')}
      </p>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!title.trim() || !date}
          className="kw-btn kw-btn-small flex-1"
        >
          {t('record.planSubmit')}
        </button>
        <button
          type="button"
          onClick={() => setForm(false)}
          className="kw-btn kw-btn-quiet kw-btn-small flex-1"
        >
          {t('record.cancel')}
        </button>
      </div>
    </form>
  );
}

function EntryForm({
  kind,
  existing,
  subjectId,
  householdId,
  mode,
  name,
  onDone,
}: {
  kind: RecordKind;
  existing: LocalRecordEntry | null;
  subjectId: string;
  householdId: string | null;
  mode: 'own' | 'child' | 'readOnly';
  name: string | undefined;
  onDone: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const baseId = useId();
  const [values, setValues] = useState<Record<string, string>>(existing?.fields ?? {});
  const [visibility, setVisibility] = useState<RecordVisibility>(
    existing?.visibility === 'child' ? 'child' : 'adults',
  );
  const [error, setError] = useState(false);
  const primary = recordFields(kind).find((field) => 'primary' in field);
  const ready = Boolean(primary && values[primary.key]?.trim());

  return (
    <form
      aria-label={t('record.formLabel', { kind: t(`record.kinds.${kind}`) })}
      className="flex flex-col gap-2 rounded-lg border border-line p-2"
      onSubmit={(event) => {
        event.preventDefault();
        setError(false);
        saveRecordEntry(
          {
            householdId: mode === 'child' ? householdId : null,
            subjectId,
            visibility: mode === 'child' ? visibility : 'me',
            kind,
            fields: values,
          },
          existing,
          db,
        )
          .then(onDone)
          .catch((cause: unknown) => {
            if (cause instanceof InvalidRecord) setError(true);
            else throw cause;
          });
      }}
    >
      <p className="kw-label">{t(`record.kinds.${kind}`)}</p>
      {kind === 'medication' ? (
        <p className="text-xs text-ink-muted">{t('record.medicationHint')}</p>
      ) : null}
      {kind === 'size' ? <p className="text-xs text-ink-muted">{t('record.sizeHint')}</p> : null}
      {recordFields(kind).map((field) => {
        const id = `${baseId}-${field.key}`;
        const common = {
          id,
          value: values[field.key] ?? '',
          onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setValues({ ...values, [field.key]: event.target.value }),
          className: 'kw-field',
          required: 'primary' in field,
        };
        return (
          <label key={field.key} htmlFor={id} className="flex flex-col gap-1">
            <span className="text-xs text-ink-muted">
              {t(`record.fields.${kind}.${field.key}`)}
            </span>
            {field.type === 'multiline' ? (
              <textarea {...common} rows={2} maxLength={2000} />
            ) : (
              <input
                {...common}
                type={field.type === 'phone' ? 'tel' : field.type === 'date' ? 'date' : 'text'}
                maxLength={field.type === 'phone' ? 40 : 200}
                {...(field.type === 'phone'
                  ? { autoComplete: 'tel', inputMode: 'tel' as const }
                  : {})}
              />
            )}
          </label>
        );
      })}
      {mode === 'child' ? (
        <fieldset className="flex flex-col gap-1">
          <legend className="text-xs text-ink-muted">{t('record.whoSees')}</legend>
          <div className="flex flex-wrap gap-2">
            {(['adults', 'child'] as const).map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={visibility === option}
                onClick={() => setVisibility(option)}
                className="kw-toggle"
              >
                {t(`record.visibility.${option}`, { name: name ?? '' })}
              </button>
            ))}
          </div>
        </fieldset>
      ) : null}
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {t('record.invalid')}
        </p>
      ) : null}
      <div className="flex gap-2">
        <button type="submit" disabled={!ready} className="kw-btn kw-btn-small flex-1">
          {t('record.save')}
        </button>
        <button type="button" onClick={onDone} className="kw-btn kw-btn-quiet kw-btn-small flex-1">
          {t('record.cancel')}
        </button>
      </div>
    </form>
  );
}

/**
 * Die eigene Akte in den Einstellungen — vorerst dort, bis die Räume stehen
 * (Ich). Ein Kind sieht hier nur lesend, was die Eltern freigegeben haben.
 */
export function RecordTile({
  me,
  child,
  onSetupEncryption,
}: {
  me: string;
  child: boolean;
  onSetupEncryption?: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="settings-record" className="kw-tile flex flex-col gap-2">
      <h4 id="settings-record" className="kw-kicker">
        {child ? t('record.sharedHeading') : t('record.ownHeading')}
      </h4>
      <p className="text-xs text-ink-muted">
        {child ? t('record.sharedIntro') : t('record.ownIntro')}
      </p>
      <RecordPanel
        subjectId={me}
        householdId={null}
        mode={child ? 'readOnly' : 'own'}
        {...(onSetupEncryption ? { onSetupEncryption } : {})}
      />
    </section>
  );
}
